import React, { useState, useEffect } from "react";
import { useTheme } from "@/lib/theme-context";
import SiteHeader from "@/polymet/components/site-header";
import SiteFooter from "@/polymet/components/site-footer";
import ThemeCssVariables from "@/polymet/components/theme-css-variables";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";

// Breakpoint constants for responsive design
export const MOBILE_BREAKPOINT = '(max-width: 640px)';
export const TABLET_BREAKPOINT = '(max-width: 1024px)';
export const DESKTOP_BREAKPOINT = '(min-width: 1025px)';

interface MainLayoutProps {
  children: React.ReactNode;
  className?: string;
}

export default function MainLayout({ children, className = '' }: MainLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const { theme, setTheme } = useTheme();
  const isMobile = useMediaQuery({ query: MOBILE_BREAKPOINT });
  const isTablet = useMediaQuery({ query: TABLET_BREAKPOINT });
  const isDesktop = useMediaQuery({ query: DESKTOP_BREAKPOINT });
  const { toast } = useToast();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when screen size changes to desktop
  useEffect(() => {
    if (!isMobile && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile, isMobileMenuOpen]);

  const handleThemeToggle = () => {
    try {
      setTheme(theme === 'dark' ? 'standard' : 'dark');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to toggle theme",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <ThemeCssVariables />
      <div className={`flex min-h-screen flex-col bg-background text-foreground ${className}`}>
        <SiteHeader 
          onToggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isScrolling={isScrolling}
          isMobile={isMobile}
          isTablet={isTablet}
          isDesktop={isDesktop}
        />
        
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="fixed inset-y-0 right-0 w-full max-w-[280px] sm:max-w-[320px] bg-background z-50 shadow-lg overflow-y-auto"
            >
              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold">Menu</h2>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 hover:bg-accent rounded-full transition-colors"
                    aria-label="Close menu"
                  >
                    <svg
                      className="h-5 w-5 sm:h-6 sm:w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <nav className="space-y-3 sm:space-y-4">
                  {/* Add your mobile navigation items here */}
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <main className="flex-1 overflow-x-hidden">
          <div className="w-full">
            {children}
          </div>
        </main>

        <SiteFooter 
          isMobile={isMobile}
          isTablet={isTablet}
          isDesktop={isDesktop}
          onThemeToggle={handleThemeToggle}
        />
      </div>
    </>
  );
}
